<template>
  <div class="store-settings-container">
    <h1 class="page-title">门店设置</h1>
    
    <el-card v-loading="loading" element-loading-text="加载中..." class="content-card">
      <div class="card-header">
        <el-button type="primary" @click="saveSettings" :loading="saving">
          <el-icon><Check /></el-icon>保存设置
        </el-button>
      </div>
      
      <div class="card-content">
        <el-form label-width="120px" :model="formData" :rules="rules" ref="formRef">
          <el-form-item label="门店名称" prop="name">
            <el-input v-model="formData.name" placeholder="请输入门店名称" maxlength="100" show-word-limit />
          </el-form-item>
          
          <el-form-item label="联系电话" prop="phone">
            <el-input v-model="formData.phone" placeholder="请输入联系电话" maxlength="20" show-word-limit />
          </el-form-item>
          
          <el-form-item label="门店地址" prop="address">
            <el-input v-model="formData.address" placeholder="请输入门店地址" :rows="2" type="textarea" resize="vertical" />
          </el-form-item>
          
          <el-form-item label="营业时间" prop="business_hours">
            <el-input v-model="formData.business_hours" placeholder="请输入营业时间，例如：周一至周日 9:00-21:00" :rows="2" type="textarea" resize="vertical" />
          </el-form-item>
          
          <el-form-item label="经纬度">
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item prop="longitude" label-width="0">
                  <el-input v-model="formData.longitude" placeholder="经度" />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item prop="latitude" label-width="0">
                  <el-input v-model="formData.latitude" placeholder="纬度" />
                </el-form-item>
              </el-col>
            </el-row>
          </el-form-item>
          
          <el-form-item label="门店图片">
            <!-- 图片大小提示 -->
            <el-alert
              title="图片上传提示"
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 16px;"
            >
              <template #default>
                <div>基于当前服务器配置，建议您将上传的图片大小控制在200KB以下，以确保页面加载速度和最佳显示效果。</div>
              </template>
            </el-alert>
            
            <div class="store-image-upload">
              <!-- 只有当没有图片时才显示上传按钮 -->
              <el-upload
                v-if="!formData.image"
                :http-request="customUpload"
                :before-upload="beforeImageUpload"
                limit="1"
                list-type="picture-card"
              >
                <el-icon><Plus /></el-icon>
                <template #tip>
                  <div class="el-upload__tip">
                    只能上传jpg/png文件，且不超过2MB
                  </div>
                </template>
              </el-upload>
              
              <!-- 显示已上传的图片 -->
              <div v-else class="uploaded-image-container">
                <el-image
                  :src="formData.image"
                  style="width: 100px; height: 100px;"
                  fit="cover"
                />
                <el-button type="text" @click="removeImage" style="margin-top: 10px; color: #f56c6c;">
                  更换图片
                </el-button>
              </div>
            </div>
          </el-form-item>
          
          <el-form-item label="门店简介">
            <el-input v-model="formData.intro" placeholder="请输入门店简介" :rows="4" type="textarea" resize="vertical" />
          </el-form-item>
          
          <el-form-item label="门店状态" prop="status">
            <div class="status-wrapper">
              <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" />
            </div>
          </el-form-item>
          <div class="form-help-text-container">
            <div class="form-help-text">
              关闭后，店铺将变为闭店状态，小程序到店相关功能将无法使用
            </div>
          </div>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { Check, Plus } from '@element-plus/icons-vue';
import { ElMessage, ElForm, ElInput, ElUpload, ElImage, ElSwitch, ElRow, ElCol } from 'element-plus';
import request from '../../api/axios';

const saving = ref(false);
const loading = ref(true);
const formRef = ref(null);
const formData = reactive({
  name: '',
  phone: '',
  address: '',
  business_hours: '',
  latitude: null,
  longitude: null,
  image: '',
  intro: '',
  status: 1
});

const rules = {
  name: [{ required: true, message: '请输入门店名称', trigger: 'blur' }],
  phone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }],
  address: [{ required: true, message: '请输入门店地址', trigger: 'blur' }],
  business_hours: [{ required: true, message: '请输入营业时间', trigger: 'blur' }],
  longitude: [{ required: true, message: '请输入经度', trigger: 'blur'}],
  latitude: [{ required: true, message: '请输入纬度', trigger: 'blur'}],
  status: [{ required: true, message: '请选择门店状态', trigger: 'change' }]
};

// 获取门店设置
const getStoreSettings = async () => {
  loading.value = true;
  try {
    const response = await request({
      url: '/manage/store-settings',
      method: 'get'
    });
    
    if (response.code === 200) {
      // 深拷贝响应数据并转换数值类型
      const data = {...response.data};
      // 确保纬度和经度是数值类型
      if (data.latitude) {
        data.latitude = parseFloat(data.latitude);
      }
      if (data.longitude) {
        data.longitude = parseFloat(data.longitude);
      }
      // 确保状态是数值类型
      if (data.status !== undefined && typeof data.status === 'string') {
        data.status = parseInt(data.status);
      }
      
      Object.assign(formData, data);
    }
  } catch (error) {
    ElMessage.error('获取门店设置失败');
    console.error('获取门店设置失败:', error);
  } finally {
    loading.value = false;
  }
};

// 保存门店设置
const saveSettings = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        saving.value = true;
        
        const response = await request({
          url: '/manage/store-settings',
          method: 'put',
          data: formData
        });
        
        if (response.code === 200) {
          ElMessage.success('保存成功');
        }
      } catch (error) {
        ElMessage.error('保存失败');
        console.error('保存门店设置失败:', error);
      } finally {
        saving.value = false;
      }
    }
  });
};

// 图片上传成功处理
const handleImageUpload = (response) => {
  if (response.errno === 0 && response.data && response.data.length > 0) {
    formData.image = response.data[0].url;
    ElMessage.success('图片上传成功');
  } else {
    ElMessage.error('图片上传失败');
  }
};

// 自定义图片上传函数
const customUpload = async (options) => {
  try {
    const { file } = options;
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    // 获取API基础URL
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
    const domainMatch = apiBaseUrl.match(/^(https?:\/\/[^\/]+)/);
    const baseDomain = domainMatch ? domainMatch[1] : window.location.origin;
    
    const response = await request({
      url: '/manage/upload/image',
      method: 'post',
      data: uploadFormData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.errno === 0 && response.data && response.data.length > 0) {
      let imgUrl = response.data[0].url;
      // 确保返回的是完整的绝对路径
      if (imgUrl && !imgUrl.startsWith('http://') && !imgUrl.startsWith('https://')) {
        imgUrl = `${baseDomain}${imgUrl}`;
      }
      formData.image = imgUrl;
      ElMessage.success('图片上传成功');
      options.onSuccess();
    } else {
      ElMessage.error('图片上传失败');
      options.onError();
    }
  } catch (error) {
    console.error('图片上传失败:', error);
    ElMessage.error('图片上传失败');
    options.onError();
  }
};

// 图片上传前验证
const beforeImageUpload = (file) => {
  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
  const isLt2M = file.size / 1024 / 1024 < 2;
  
  if (!isJpgOrPng) {
    ElMessage.error('只能上传 JPG/PNG 格式的图片');
  }
  if (!isLt2M) {
    ElMessage.error('图片大小不能超过 2MB');
  }
  
  return isJpgOrPng && isLt2M;
};

// 清除已上传的图片
const removeImage = () => {
  formData.image = '';
  ElMessage.success('已清除图片，您可以上传新的图片');
};

onMounted(() => {
  getStoreSettings();
});
</script>

<style scoped>
.store-settings-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.card-content {
  padding: 20px 0;
}

.status-wrapper {
    display: inline-block;
    margin-bottom: 10px;
  }

  .form-help-text-container {
    margin-left: 120px; /* 与表单标签宽度一致 */
    margin-top: -20px;
    margin-bottom: 20px;
  }

  .form-help-text {
    font-size: 12px;
    color: #E6A23C;
    line-height: 1.5;
    display: block;
    width: 100%;
    max-width: 500px;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
  }
</style>